import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, ChevronDown, ChevronUp, Target, TrendingUp } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PaycheckStrip
 * -------------
 * Compact "next N days income" widget that lives at the top of the
 * /portfolio view. Designed to be the first thing users see — gives
 * them a "this is what's hitting your bank" snapshot rather than the
 * abstract annualized yield numbers.
 *
 * Default window: 30 days.
 * Toggleable to 60 / 90 days for longer horizons.
 *
 * Mobile-first layout:
 *   - Phones: collapses to a single banner showing the next payment
 *     and total. Tap to expand.
 *   - Tablets/desktop: shows the 5 nearest payments inline + run-rate
 *     vs goal progress bar.
 *
 * Backend: GET /api/portfolio/paycheck?days=30
 */
export default function PaycheckStrip({ refreshKey = 0, onStockClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [expanded, setExpanded] = useState(false);
  // Round P146·5 — which daily-rollup rows are expanded to show their
  // individual payouts. Keyed by ticker so multiple daily payers can
  // be open at once.
  const [openDaily, setOpenDaily] = useState({});
  const toggleDaily = (tk) => setOpenDaily((o) => ({ ...o, [tk]: !o[tk] }));
  const scopeParams = usePortfolioScopeParams({ withWatchlistFlag: true });
  const scopeKey = `${scopeParams.portfolio_id || 'all'}|${scopeParams.include_watchlist === false ? 'po' : 'both'}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/paycheck`, {
      params: { days: windowDays, ...scopeParams },
      withCredentials: true,
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays, refreshKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const payments = data?.payments || [];
  const dailyRollups = data?.daily_rollups || [];
  const total = data?.total || 0;
  const monthlyRun = data?.monthly_run_rate || 0;
  const monthlyGoal = data?.monthly_goal || 0;
  const goalPct = data?.goal_pct || 0;

  // Format a YYYY-MM-DD as e.g. "May 5". Assumes UTC date so we
  // don't get an off-by-one from local timezone parsing.
  const formatShort = (iso) => {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    } catch { return iso; }
  };

  const fmtMoney = (n) => {
    if (n == null || !isFinite(n)) return '—';
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const next = payments[0];
  const visible = expanded ? payments : payments.slice(0, 5);

  // Don't render at all if user hasn't built a portfolio yet —
  // PortfolioSummary already shows the empty-state guidance.
  if (!loading && !data) return null;
  if (data && payments.length === 0 && dailyRollups.length === 0 && total === 0 && monthlyRun === 0) return null;

  // Round P145 (Feb 2026) — Compact daily-payer row formatter.
  // Renders "SATA · Daily · $12.90 · 25 pays · Aug 26–Sep 24" as a
  // single row that stands in for what would otherwise be 25
  // per-day rows blowing out the mobile view.
  const _formatDateRange = (fromIso, toIso) => {
    const fmt = (iso) => {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    };
    if (!fromIso || !toIso || fromIso === toIso) return fmt(fromIso);
    return `${fmt(fromIso)} – ${fmt(toIso)}`;
  };

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden mb-4"
      data-testid="paycheck-strip"
    >
      {/* Header row — always visible */}
      <div className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap border-b border-border/50 bg-muted/20">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <h3 className="text-sm font-bold tracking-tight" data-testid="paycheck-title">
            Next {windowDays} Days Income
          </h3>
          <span className="text-[11px] text-muted-foreground hidden sm:inline">
            {(() => {
              // Round P146·5 fix — count the rolled-up daily payouts too
              // so the label matches the window total.
              const totalPays = payments.length + dailyRollups.reduce((s, r) => s + (r.count || 0), 0);
              return `${totalPays} ${totalPays === 1 ? 'payment' : 'payments'}`;
            })()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`px-2 py-0.5 rounded text-[11px] font-mono font-semibold transition-colors ${
                windowDays === d
                  ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60 dark:ring-emerald-700/60'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`paycheck-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Top stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 px-4 py-3 border-b border-border/50">
        <div data-testid="paycheck-total">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
            Window Total
          </p>
          <p className="text-base sm:text-lg font-bold font-mono text-emerald-600 dark:text-emerald-400">
            {loading ? '...' : fmtMoney(total)}
          </p>
        </div>
        <div data-testid="paycheck-monthly-run">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Monthly Run Rate
          </p>
          <p className="text-base sm:text-lg font-bold font-mono">
            {loading ? '...' : fmtMoney(monthlyRun)}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1" data-testid="paycheck-goal-progress">
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1">
            <Target className="w-3 h-3" />
            Goal Progress
          </p>
          {monthlyGoal > 0 ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-base sm:text-lg font-bold font-mono">
                  {goalPct.toFixed(0)}%
                </p>
                <p className="text-[11px] text-muted-foreground font-mono">
                  of {fmtMoney(monthlyGoal)}/mo
                </p>
              </div>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-1">
                <div
                  className={`h-full rounded-full transition-all ${
                    goalPct >= 100
                      ? 'bg-emerald-500'
                      : goalPct >= 75
                      ? 'bg-emerald-400'
                      : goalPct >= 50
                      ? 'bg-amber-400'
                      : 'bg-rose-400'
                  }`}
                  style={{ width: `${Math.min(100, goalPct).toFixed(1)}%` }}
                />
              </div>
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground italic mt-1">
              Set a monthly goal in Portfolio Summary →
            </p>
          )}
        </div>
      </div>

      {/* Payment list */}
      {payments.length === 0 ? (
        <div className="px-4 py-5 text-center text-xs text-muted-foreground italic" data-testid="paycheck-empty">
          {loading ? 'Loading payment schedule…' : 'No upcoming payments in this window.'}
        </div>
      ) : (
        <>
          <div className="px-4 py-2 flex items-center justify-between gap-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
              Upcoming Payments
            </span>
            {next && !expanded && (
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                Next: <span className="font-mono font-semibold text-foreground">{formatShort(next.date)} · {fmtMoney(next.amount)}</span>
              </span>
            )}
          </div>
          <ul className="divide-y divide-border/40" data-testid="paycheck-payments-list">
            {/* Round P145 — daily-payer rollups render first as a
                single row per ticker with a cyan "Daily" badge, the
                accumulated total across the window, the pay count,
                and the date-range. Only shown when data.daily_rollups
                has entries (SATA-type first-of-cadence stocks). */}
            {dailyRollups.map((r) => (
              <li
                key={`daily-${r.ticker}`}
                className="bg-cyan-50/30 dark:bg-cyan-950/10"
                data-testid={`paycheck-daily-row-${r.ticker}`}
              >
                <div className="px-4 py-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[9px] font-bold px-1.5 py-[2px] rounded-sm bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border border-cyan-300/60 dark:border-cyan-700/60 w-14 text-center shrink-0">
                      DAILY
                    </span>
                    <button
                      type="button"
                      onClick={() => onStockClick?.(r.ticker)}
                      className="text-xs font-mono font-semibold hover:text-primary hover:underline transition-colors cursor-pointer"
                      title={`Open ${r.ticker} stock modal`}
                      data-testid={`paycheck-daily-ticker-${r.ticker}`}
                    >
                      {r.ticker}
                    </button>
                    <span
                      className="text-[10px] text-muted-foreground truncate"
                      title={`${r.count} daily payouts from ${r.first_date} through ${r.last_date}`}
                    >
                      {r.count} pays · {_formatDateRange(r.first_date, r.last_date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono font-semibold text-emerald-700 dark:text-emerald-300">
                      {fmtMoney(r.amount)}
                    </span>
                    {(r.payouts || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleDaily(r.ticker)}
                        className="p-0.5 rounded hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 transition-colors"
                        title={openDaily[r.ticker] ? 'Hide individual payouts' : `Show all ${r.count} payouts`}
                        aria-expanded={!!openDaily[r.ticker]}
                        data-testid={`paycheck-daily-toggle-${r.ticker}`}
                      >
                        {openDaily[r.ticker] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
                {/* Round P146·5 — per-payout dropdown. Scrollable so a
                    full month (~22 business days) doesn't blow out the
                    strip, especially on phones. */}
                {openDaily[r.ticker] && (r.payouts || []).length > 0 && (
                  <ul
                    className="mx-4 mb-2 rounded-md border border-cyan-200/60 dark:border-cyan-800/40 divide-y divide-cyan-200/40 dark:divide-cyan-800/30 max-h-44 overflow-y-auto bg-background/60"
                    data-testid={`paycheck-daily-payouts-${r.ticker}`}
                  >
                    {r.payouts.map((p) => (
                      <li
                        key={p.date}
                        className="px-3 py-1 flex items-center justify-between gap-2 text-[11px] font-mono"
                        data-testid={`paycheck-daily-payout-${r.ticker}-${p.date}`}
                      >
                        <span className="text-muted-foreground">{formatShort(p.date)}</span>
                        <span className="flex items-center gap-1.5">
                          {!p.confirmed && (
                            <span className="text-[8px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400" title="Projected — not yet confirmed">est</span>
                          )}
                          <span className="font-semibold tabular-nums">{fmtMoney(p.amount)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {visible.map((p) => (
              <li
                key={p.date + p.tickers.join('-')}
                className="px-4 py-2 flex items-center justify-between gap-3"
                data-testid={`paycheck-row-${p.date}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-mono font-semibold w-14 shrink-0 text-muted-foreground">
                    {formatShort(p.date)}
                  </span>
                  <span className="text-xs font-mono truncate flex flex-wrap items-baseline gap-x-1" title={p.tickers.join(', ')}>
                    {p.tickers.slice(0, 4).map((tk, i) => (
                      <span key={tk}>
                        {/* Each ticker is a click-to-open-modal button.
                            Hover gets a subtle underline + primary
                            color so the affordance is obvious without
                            crowding the row visually. */}
                        <button
                          type="button"
                          onClick={() => onStockClick?.(tk)}
                          className="font-mono hover:text-primary hover:underline transition-colors cursor-pointer"
                          title={`Open ${tk} stock modal`}
                          data-testid={`paycheck-ticker-${tk}-${p.date}`}
                        >{tk}</button>
                        {i < Math.min(p.tickers.length, 4) - 1 ? ',' : ''}
                      </span>
                    ))}
                    {p.tickers.length > 4 && (
                      <span className="text-muted-foreground ml-1">+{p.tickers.length - 4}</span>
                    )}
                  </span>
                  {!p.confirmed && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400"
                      title="Projected — not yet confirmed by the company"
                    >
                      est
                    </span>
                  )}
                </div>
                <span className="text-xs font-mono font-bold tabular-nums shrink-0">
                  {fmtMoney(p.amount)}
                </span>
              </li>
            ))}
          </ul>
          {payments.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="w-full px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors flex items-center justify-center gap-1 border-t border-border/40"
              data-testid="paycheck-toggle-expand"
            >
              {expanded ? (
                <>Show less <ChevronUp className="w-3 h-3" /></>
              ) : (
                <>Show all {payments.length} payments <ChevronDown className="w-3 h-3" /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
